<script>
import { mapGetters } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import LeadCardPanel from 'dashboard/components/widgets/conversation/LeadCardPanel.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
    LeadCardPanel,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      windowWidth,
    };
  },
  data() {
    return {
      showSearchModal: false,
      viewingHeartbeat: null,
      lastViewedConversationId: null,
      // Carrusel celular (Victor 2026-06-02): false = chat, true = info del
      // cliente. Se desliza ⟵ para ver info, ⟶ para volver al chat / a la lista.
      mobileInfoOpen: false,
      swipeStartX: null,
      swipeStartY: null,
      swipeDx: 0,
      swiping: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      currentAccount: 'getCurrentAccount',
    }),
    // D2 · 2026-05-19 · Panel derecho visible para admin o super admin (antes
    // solo super · regla 2026-05-19). Agentes regulares no lo ven ni el trigger.
    isAdminOrSuperAdmin() {
      return (
        this.currentAccount?.super_admin === true ||
        this.currentAccount?.role === 'administrator'
      );
    },
    // Móvil tipo WhatsApp (Victor 2026-06-02): en celular (< 768px) se muestra
    // SOLO la lista o SOLO el chat, nunca ambos. Al abrir un cliente se ve el
    // chat a pantalla completa y se vuelve con la flecha de atrás.
    isMobile() {
      return this.windowWidth < 768;
    },
    showConversationList() {
      // Móvil: solo la lista cuando NO hay chat abierto.
      if (this.isMobile) return !this.currentChat.id;
      // V4 · 2026-05-13 (Victor): siempre mostrar el wrapper · cuando hay
      // conversation abierta colapsa a rail 64px (OC-2). Antes en EXPANDED
      // se ocultaba todo · ahora se ve el strip de avatares en ambos layouts.
      return true;
    },
    showMessageView() {
      // Móvil: solo el chat cuando hay uno abierto (pantalla completa).
      if (this.isMobile) return !!this.currentChat.id;
      // V5-P1 · 2026-05-15 (Victor) · Side-by-side: mostrar conversación SIEMPRE
      // que haya conversationId · funciona en ambos layouts (no oculta chatlist).
      // 2026-05-22 (Victor): mostramos SIEMPRE el panel derecho en ambos layouts
      // (condensado y expandido): conversación si hay una abierta, o el estado
      // vacío estilo Kommo si no. Así nunca queda el panel en negro.
      return true;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },

    isLeadPanelOpen() {
      const { is_lead_panel_open: isOpen } = this.uiSettings;
      return isOpen !== false;
    },
    // Estilo del carrusel móvil: 0% = chat, -50% = info; sigue el dedo mientras
    // se arrastra (limitado entre chat e info para que se vea natural).
    mobileTrackStyle() {
      const base = this.mobileInfoOpen ? -50 : 0;
      let pos = base;
      if (this.swiping) {
        const w = window.innerWidth || 360;
        pos = Math.max(-50, Math.min(0, base + (this.swipeDx / w) * 50));
      }
      return {
        transform: `translateX(${pos}%)`,
        transition: this.swiping
          ? 'none'
          : 'transform .3s cubic-bezier(.3,0,.2,1)',
      };
    },
  },
  watch: {
    conversationId(newId, oldId) {
      this.fetchConversationIfUnavailable();
      this.handleViewingChange(newId, oldId);
      // Al abrir otro chat, arrancar en el panel del chat (no en info).
      this.mobileInfoOpen = false;
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    // Victor 2026-05-30: refrescar inboxes al entrar a un chat. Sin esto, si el
    // agente cambia el switcher de sucursal y luego navega al chat desde el
    // Kanban (que no carga inboxes), la lista queda vacía y EmptyState muestra
    // "no eres parte de ninguna bandeja" aunque sí lo sea. Garantiza que
    // cualquier agente pueda entrar a chats de cualquier sucursal accesible.
    this.$store.dispatch('inboxes/get');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    if (this.conversationId) this.handleViewingChange(this.conversationId, null);
    window.addEventListener('beforeunload', this.notifyViewingOff);
  },

  beforeUnmount() {
    this.notifyViewingOff();
    if (this.viewingHeartbeat) clearInterval(this.viewingHeartbeat);
    window.removeEventListener('beforeunload', this.notifyViewingOff);
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    toggleLeadPanel() {
      this.updateUISettings({ is_lead_panel_open: !this.isLeadPanelOpen });
    },

    handleViewingChange(newId, oldId) {
      if (oldId && oldId !== newId) {
        ConversationApi.toggleViewing({ conversationId: oldId, status: 'off' }).catch(() => {});
      }
      if (this.viewingHeartbeat) {
        clearInterval(this.viewingHeartbeat);
        this.viewingHeartbeat = null;
      }
      if (!newId) return;
      this.lastViewedConversationId = newId;
      ConversationApi.toggleViewing({ conversationId: newId, status: 'on' }).catch(() => {});
      this.viewingHeartbeat = setInterval(() => {
        ConversationApi.toggleViewing({ conversationId: newId, status: 'on' }).catch(() => {});
      }, 20000);
    },

    notifyViewingOff() {
      const id = this.lastViewedConversationId;
      if (!id) return;
      try {
        ConversationApi.toggleViewing({ conversationId: id, status: 'off' });
      } catch (e) {
        // silencioso
      }
    },

    // ===== Carrusel celular: Chat ↔ Info, y ⟶ para volver a la lista =====
    mobileBackToList() {
      const { params, name } = this.$route;
      const typeMap = {
        conversation_through_mentions: 'mention',
        conversation_through_unattended: 'unattended',
      };
      this.$router.push(
        conversationListPageURL({
          accountId: this.accountId,
          inboxId: params.inbox_id,
          label: params.label,
          teamId: params.teamId,
          conversationType: typeMap[name],
          customViewId: params.id,
        })
      );
    },
    onSwipeStart(e) {
      const t = e.touches[0];
      this.swipeStartX = t.clientX;
      this.swipeStartY = t.clientY;
      this.swipeDx = 0;
      this.swiping = false;
    },
    onSwipeMove(e) {
      if (this.swipeStartX === null) return;
      const t = e.touches[0];
      const dx = t.clientX - this.swipeStartX;
      const dy = t.clientY - this.swipeStartY;
      // Decide horizontal vs vertical una sola vez (no robar el scroll del chat).
      if (!this.swiping) {
        if (Math.abs(dx) < 14) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          this.swipeStartX = null;
          return;
        }
        this.swiping = true;
      }
      this.swipeDx = dx;
    },
    onSwipeEnd() {
      if (!this.swiping) {
        this.swipeStartX = null;
        this.swipeDx = 0;
        return;
      }
      const w = window.innerWidth || 360;
      const th = w * 0.22;
      const dx = this.swipeDx;
      if (this.mobileInfoOpen) {
        if (dx > th) this.mobileInfoOpen = false; // ⟶ vuelve al chat
      } else if (dx < -th) {
        this.mobileInfoOpen = true; // ⟵ ver info del cliente
      } else if (dx > th) {
        this.mobileBackToList(); // ⟶ volver a la lista
      }
      this.swipeStartX = null;
      this.swipeDx = 0;
      this.swiping = false;
    },
  },
};
</script>

<template>
  <section class="flex flex-col w-full h-full min-w-0">
    <!-- Barra del módulo Chat (Victor 2026-06-11 · Fase 2): de lado a lado, arriba
         de la lista y el chat. El header (título + buscador + iconos) y los filtros
         se teletransportan aquí desde ChatList en escritorio. En móvil queda igual. -->
    <div
      v-if="!isMobile"
      id="chatModuleTopbar"
      class="shrink-0 w-full border-b border-n-weak bg-n-background"
    />
    <div class="flex flex-1 min-h-0 w-full">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <LeadCardPanel
      v-if="!isMobile && showMessageView && currentChat.id && isLeadPanelOpen"
      @toggle="toggleLeadPanel"
    />
    <button
      v-if="!isMobile && showMessageView && currentChat.id && !isLeadPanelOpen"
      type="button"
      class="shrink-0 w-6 bg-n-background hover:bg-n-alpha-2 border-r border-n-weak flex items-center justify-center text-n-slate-11 hover:text-n-slate-12 transition-colors"
      title="Mostrar tarjeta de lead"
      @click="toggleLeadPanel"
    >
      <span class="text-base leading-none">›</span>
    </button>
    <ConversationBox
      v-if="showMessageView && !isMobile"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <!-- Celular (Victor 2026-06-02): carrusel Chat ↔ Info del cliente. Desliza
         ⟵ para ver la info del lead, ⟶ para volver al chat / a la lista. -->
    <div
      v-if="isMobile && currentChat.id"
      class="flex-1 min-w-0 h-full overflow-hidden relative"
      @touchstart.passive="onSwipeStart"
      @touchmove.passive="onSwipeMove"
      @touchend="onSwipeEnd"
    >
      <div class="flex h-full w-[200%]" :style="mobileTrackStyle">
        <div class="w-1/2 h-full min-w-0 flex">
          <ConversationBox
            :inbox-id="inboxId"
            :is-on-expanded-layout="isOnExpandedLayout"
          >
            <SidepanelSwitch v-if="currentChat.id" />
          </ConversationBox>
        </div>
        <div class="w-1/2 h-full min-w-0 flex">
          <LeadCardPanel @toggle="mobileInfoOpen = false" />
        </div>
      </div>
    </div>
    </div>
    <!-- D2 · 2026-05-12 PM · Panel contacto slide-out solo super admin -->
    <template v-if="shouldShowSidebar && isAdminOrSuperAdmin">
      <!-- Edge invisible · 12px hover zone -->
      <div class="csidebar-hover-edge"></div>
      <!-- Hint sutil dorado (4×50px en el centro) -->
      <div class="csidebar-edge-hint"></div>
      <!-- Wrapper position:fixed que slidea -->
      <div class="csidebar-slide-wrapper">
        <ConversationSidebar :current-chat="currentChat" />
      </div>
    </template>
    <CmdBarConversationSnooze />
  </section>
</template>

<style lang="scss" scoped>
/* D2 · 2026-05-12 PM · Hover edge invisible para panel de Contacto.
   Solo super_admin (gate en v-if del template). El panel está fuera del
   flex layout normal · ConversationBox se expande al espacio liberado. */

/* Zona invisible que detecta hover del mouse · 12px en el borde derecho */
.csidebar-hover-edge {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 12px;
  z-index: 80;
}

/* Lengüeta sutil dorada · indicador mínimo de que existe el panel */
.csidebar-edge-hint {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 50px;
  background: rgba(251,191,36,0.30);
  border-radius: 4px 0 0 4px;
  z-index: 81;
  transition: width 0.18s ease, background 0.18s ease;
  pointer-events: none;
}

/* Panel · position:fixed · slidea desde -380px hasta 0 */
.csidebar-slide-wrapper {
  position: fixed;
  right: -380px;
  top: 0;
  bottom: 0;
  width: 340px;
  z-index: 90;
  transition: right 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: -8px 0 32px rgba(0,0,0,0.6);
}

/* Open al hover del edge invisible o del panel */
.csidebar-hover-edge:hover ~ .csidebar-slide-wrapper,
.csidebar-slide-wrapper:hover {
  right: 0;
}

/* Hint se intensifica al hover del edge */
.csidebar-hover-edge:hover ~ .csidebar-edge-hint {
  width: 6px;
  background: rgba(251,191,36,0.65);
}

/* El ConversationSidebar dentro del wrapper debe llenar el alto completo */
.csidebar-slide-wrapper :deep(> *) {
  height: 100%;
}
</style>
